import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination } from '@mui/material';
import { Chip, Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { Eye as ViewIcon } from 'lucide-react';
import { scheduleService } from '../../services/scheduleService';
import { useNotificationStore } from '../../store/notificationStore';
import type { ScheduleExecutionDto, ScheduleExecutionFilters } from '../../types/schedule';
import type { ExecutionStatus } from '../../types/schedule';
import { ExecutionStatuses } from '../../types/schedule';

interface ScheduleExecutionHistoryProps {
  scheduleId: string;
}

/**
 * Component to display schedule execution history.
 */
export const ScheduleExecutionHistory = ({ scheduleId }: ScheduleExecutionHistoryProps) => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [executions, setExecutions] = useState<ScheduleExecutionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<ScheduleExecutionFilters>({
    pageNumber: 1,
    pageSize: 10,
  });

  useEffect(() => {
    loadExecutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, filters.pageNumber, filters.status]);

  const loadExecutions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await scheduleService.getScheduleExecutions(scheduleId, filters);
      setExecutions(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load execution history');
      showNotification(err.message || 'Failed to load execution history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: ExecutionStatus): 'green' | 'amber' | 'red' | 'blue' | 'default' => {
    switch (status) {
      case ExecutionStatuses.Completed:
        return 'green';
      case ExecutionStatuses.Running:
        return 'blue';
      case ExecutionStatuses.Failed:
        return 'red';
      case ExecutionStatuses.Pending:
        return 'amber';
      case ExecutionStatuses.Cancelled:
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h6 className="text-lg font-semibold mb-4">
        Execution History
      </h6>

      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between dark:bg-red-950/30 dark:border-red-800 dark:text-red-400">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Scheduled At</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Started At</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Completed At</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Retry Count</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Error</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {executions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <p className="text-sm text-muted-foreground">
                        No executions found
                      </p>
                    </td>
                  </tr>
                ) : (
                  executions.map((execution) => (
                    <tr key={execution.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="px-3 py-2">{formatDate(execution.scheduledAt)}</td>
                      <td className="px-3 py-2">{formatDate(execution.startedAt)}</td>
                      <td className="px-3 py-2">{formatDate(execution.completedAt)}</td>
                      <td className="px-3 py-2">
                        <Chip
                          label={execution.status}
                          color={getStatusColor(execution.status)}
                        />
                      </td>
                      <td className="px-3 py-2">{execution.retryCount}</td>
                      <td className="px-3 py-2">
                        {execution.errorMessage ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="text-sm text-red-500 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                                  {execution.errorMessage}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent>{execution.errorMessage}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {execution.sessionId && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/sessions/${execution.sessionId}`)}
                                >
                                  <ViewIcon size={16} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Session</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalCount > 0 && (
            <div className="flex justify-center mt-6">
              <Pagination
                count={Math.ceil(totalCount / (filters.pageSize || 10))}
                page={filters.pageNumber || 1}
                onChange={(_, page) => setFilters({ ...filters, pageNumber: page })}
                color="primary"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
